import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Typography,
  makeStyles
} from '@material-ui/core'
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet'
import ApartmentIcon from '@material-ui/icons/Apartment'
import EventNoteIcon from '@material-ui/icons/EventNote'
import ListAltIcon from '@material-ui/icons/ListAlt'
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone'
import SettingsIcon from '@material-ui/icons/Settings'
import SportsEsportsIcon from '@material-ui/icons/SportsEsports'
import StorefrontIcon from '@material-ui/icons/Storefront'
import React from 'react'

import {
  fetchAptKits,
  fetchAptRequests,
  fetchAptVendors
} from 'api/apartmentProcurement'
import {
  fetchAfterschoolPending,
  fetchAllOrders,
  fetchCapexPending,
  fetchConsumablesPending
} from 'api/procurement'
import { hqActionCounts } from 'app/pages/Apartment/Procurement/actionSummary'

// The Procurement & Expenses landing hub (user 2026-07-17): four big boxes,
// each with a very short "what needs me" summary, clicking into its area.
const useStyles = makeStyles((theme) => ({
  card: { height: '100%' },
  content: { minHeight: 150 },
  title: {
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  pending: { color: '#e65100', fontWeight: 600 },
  ok: { color: '#2e7d32' },
  muted: { color: theme.palette.text.secondary }
}))

const Pending = ({ items, allClearText }) => {
  const classes = useStyles()
  const active = items.filter((i) => i.count > 0)
  if (!active.length) {
    return (
      <Typography variant="body2" className={classes.ok}>
        ✓ {allClearText}
      </Typography>
    )
  }
  return (
    <>
      {active.map((i) => (
        <Typography key={i.label} variant="body2" className={classes.pending}>
          • {i.count} {i.label}
        </Typography>
      ))}
    </>
  )
}

const HqHome = ({ onNavigate }) => {
  const classes = useStyles()
  const [monthly, setMonthly] = React.useState(null)
  const [daily, setDaily] = React.useState(null)
  const [notif, setNotif] = React.useState(null)
  const [bySource, setBySource] = React.useState(null)
  const [catalogueCount, setCatalogueCount] = React.useState(null)
  const [vendorCount, setVendorCount] = React.useState(null)
  // Monthly Capex and Afterschool Games each have a tab but had no card here,
  // so the overview quietly under-reported what HQ has waiting (2026-08-26).
  const [capexPending, setCapexPending] = React.useState(null)
  const [gamesPending, setGamesPending] = React.useState(null)

  React.useEffect(() => {
    let alive = true
    Promise.all([
      fetchAllOrders({}).catch(() => []),
      fetchConsumablesPending({}).catch(() => ({ results: [] }))
    ]).then(([orders, consumables]) => {
      if (!alive) return
      const list = Array.isArray(orders) ? orders : orders.results || []
      setMonthly({
        orders: list.filter((o) => o.status === 'submitted').length,
        consumables: (consumables.results || consumables || []).length
      })
    })
    fetchAptRequests({
      status: 'final_approved,in_daily,handed_over',
      lines: '1'
    })
      .then((requests) => {
        if (!alive) return
        // Empty requests don't count — deleting the last line off the sheet
        // leaves the request behind, and there is nothing left to review or
        // hand over (2026-07-22). Matches the daily sheet's own strips.
        const hasLines = (r) => (r.lines || []).length > 0
        const review = requests.filter(
          (r) => r.status === 'final_approved' && hasLines(r)
        ).length
        const handOver = requests.filter(
          (r) => r.status === 'in_daily' && r.apartment && hasLines(r)
        ).length
        const openLines = requests
          .filter((r) => r.status !== 'final_approved')
          .flatMap((r) => r.lines || [])
        const undelivered = openLines.filter(
          (l) => !['delivered', 'unavailable'].includes(l.fulfilment_status)
        ).length
        setDaily({ review, handOver, undelivered })
        // Split the working set by source for the two action cards (Sounak,
        // 2026-07-30): At Apartment flow vs Centre / direct daily-sheet rows.
        setBySource(hqActionCounts(requests))
        // Overview notifications (P2): deliveries past their expected date, and
        // lines still waiting on HQ to order/dispatch.
        const today = new Date().toISOString().slice(0, 10)
        const overdue = openLines.filter(
          (l) =>
            l.expected_delivery &&
            String(l.expected_delivery).slice(0, 10) < today &&
            !['delivered', 'unavailable'].includes(l.fulfilment_status)
        ).length
        const awaiting = openLines.filter(
          (l) => (l.fulfilment_status || 'pending') === 'pending'
        ).length
        setNotif({ overdue, awaiting })
      })
      .catch(() => {
        if (!alive) return
        setDaily({ review: 0, handOver: 0, undelivered: 0 })
        setNotif({ overdue: 0, awaiting: 0 })
        setBySource(hqActionCounts([]))
      })
    fetchAptKits(false)
      .then(
        (kits) =>
          alive &&
          setCatalogueCount(kits.reduce((s, k) => s + (k.item_count || 0), 0))
      )
      .catch(() => alive && setCatalogueCount(0))
    fetchAptVendors()
      .then((vendors) => alive && setVendorCount((vendors || []).length))
      .catch(() => alive && setVendorCount(0))
    // Both endpoints answer {pending: [...]} — submissions waiting on an HQ
    // decision. A failure leaves the card at 0 rather than blanking the page.
    fetchCapexPending()
      .then((res) => alive && setCapexPending((res && res.pending) || []))
      .catch(() => alive && setCapexPending([]))
    fetchAfterschoolPending()
      .then((res) => alive && setGamesPending((res && res.pending) || []))
      .catch(() => alive && setGamesPending([]))
    return () => {
      alive = false
    }
  }, [])

  const cards = [
    {
      id: 'dashboard',
      icon: <EventNoteIcon color="primary" />,
      title: 'Monthly Cadence',
      body: monthly ? (
        <Pending
          items={[
            { count: monthly.orders, label: 'monthly orders to approve' },
            {
              count: monthly.consumables,
              label: 'consumables submissions pending'
            }
          ]}
          allClearText="No monthly approvals waiting"
        />
      ) : (
        <Typography variant="body2">…</Typography>
      ),
      footer: 'Budgets · centre orders · consumables, month by month'
    },
    {
      id: 'daily-sheet',
      icon: <ListAltIcon color="primary" />,
      title: 'Daily Sheet',
      body: daily ? (
        <Pending
          items={[
            { count: daily.review, label: 'approved requests to review & add' },
            { count: daily.handOver, label: 'to hand over to LXD' },
            { count: daily.undelivered, label: 'lines not yet delivered' }
          ]}
          allClearText="Register is fully up to date"
        />
      ) : (
        <Typography variant="body2">…</Typography>
      ),
      footer: 'The ad-hoc register — At Apartment flow + direct entries'
    },
    {
      id: 'apt-actions',
      nav: 'daily-sheet',
      icon: <ApartmentIcon color="primary" />,
      title: 'At Apartment actions',
      body: bySource ? (
        <Pending
          items={[
            { count: bySource.apartment.newIn, label: 'new orders in' },
            { count: bySource.apartment.notSent, label: 'not sent to vendor' },
            { count: bySource.apartment.overdue, label: 'overdue from vendor' }
          ]}
          allClearText="At Apartment orders all moving"
        />
      ) : (
        <Typography variant="body2">…</Typography>
      ),
      footer: 'At Apartment flow — the daily order picture for Sounak’s team'
    },
    {
      id: 'centre-actions',
      nav: 'daily-sheet',
      icon: <StorefrontIcon color="primary" />,
      title: 'Centre actions',
      body: bySource ? (
        <Pending
          items={[
            { count: bySource.centre.newIn, label: 'new orders in' },
            { count: bySource.centre.notSent, label: 'not sent to vendor' },
            { count: bySource.centre.overdue, label: 'overdue from vendor' }
          ]}
          allClearText="Centre orders all moving"
        />
      ) : (
        <Typography variant="body2">…</Typography>
      ),
      footer: 'Centre & direct daily-sheet rows — the daily order picture'
    },
    {
      id: 'capex',
      // `tab` holds the LEAF id, so navigate to the review board rather than
      // the group — that is where a pending submission is actioned.
      nav: 'capex-review',
      icon: <AccountBalanceWalletIcon color="primary" />,
      title: 'Monthly Capex',
      body: capexPending ? (
        <Pending
          items={[
            { count: capexPending.length, label: 'capex months to review' }
          ]}
          allClearText="No capex submissions waiting"
        />
      ) : (
        <Typography variant="body2">…</Typography>
      ),
      footer: 'The monthly capex cycle — month setup, budgets and review'
    },
    {
      id: 'afterschool',
      icon: <SportsEsportsIcon color="primary" />,
      title: 'Afterschool Games',
      body: gamesPending ? (
        <Pending
          items={[
            { count: gamesPending.length, label: 'game orders to review' }
          ]}
          allClearText="No game orders waiting"
        />
      ) : (
        <Typography variant="body2">…</Typography>
      ),
      footer: 'Centre stock checks and the games catalogue'
    },
    {
      id: 'notif',
      nav: 'daily-sheet',
      icon: <NotificationsNoneIcon color="primary" />,
      title: 'Notifications',
      body: notif ? (
        <Pending
          items={[
            { count: notif.overdue, label: 'deliveries overdue' },
            {
              count: notif.awaiting,
              label: 'lines awaiting HQ to order/dispatch'
            }
          ]}
          allClearText="Nothing needs attention right now"
        />
      ) : (
        <Typography variant="body2">…</Typography>
      ),
      footer:
        'The important things — overdue deliveries and lines waiting on HQ'
    },
    {
      id: 'catalogue',
      icon: <SettingsIcon color="primary" />,
      title: 'Settings',
      body: (
        <Typography variant="body2" className={classes.muted}>
          {catalogueCount == null ? '…' : `${catalogueCount} catalogue items`} ·{' '}
          {vendorCount == null ? '…' : `${vendorCount} vendors`} · SOP
        </Typography>
      ),
      footer: 'Catalogue, SOP & vendor list — the reference lists, kept current'
    }
  ]

  return (
    <Grid container spacing={2}>
      {cards.map((card) => (
        <Grid item xs={12} sm={6} key={card.id}>
          <Card variant="outlined" className={classes.card}>
            <CardActionArea onClick={() => onNavigate(card.nav || card.id)}>
              <CardContent className={classes.content}>
                <Typography variant="h6" className={classes.title}>
                  {card.icon} {card.title}
                </Typography>
                {card.body}
                <Box mt={2}>
                  <Typography variant="caption" className={classes.muted}>
                    {card.footer}
                  </Typography>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      ))}
    </Grid>
  )
}

export default HqHome
